/**
 * Sprayo CRO — hero / header handshake.
 *
 * The theme's header has a transparent mode: on the homepage it drops its
 * background and paints its logo and icons in `--color-transparent-header`,
 * which is white. That is only correct while it is sitting ON a dark hero.
 *
 * The theme's own hero sections arrange that themselves — each one carries a
 * script that pulls the header over it and toggles the class back off once
 * the hero scrolls away. With those sections disabled nothing did it, so the
 * header sat in normal flow above our hero, transparent, on a white page:
 * white logo, white cart, white burger, all invisible.
 *
 * This does the same two jobs for our hero and nothing else.
 */
class SprayoHero extends HTMLElement {
  connectedCallback() {
    this.header = document.querySelector(".wt-header");
    if (!this.header) return;

    /* Only when the theme actually wants a transparent header here. With the
       setting off, the header is a normal opaque bar and must stay put. */
    if (!this.header.dataset.transparent || !this.header.classList.contains("wt-header--v3")) return;

    /* And only for the first section on the page — a hero further down must
       not drag the header on top of whatever is above it. */
    if (!this.isFirstSection()) return;

    this.classList.add("is-overlaid");
    this.revealLogo();
    this.measure();
    this.observe();

    /* Width only. On a phone, `resize` fires every time the URL bar slides
       away during a scroll — remeasuring on that made the hero resize under
       the reader's thumb, which is the jitter this guard exists to stop. */
    this.lastWidth = window.innerWidth;
    this.onResize = () => {
      if (window.innerWidth === this.lastWidth) return;
      this.lastWidth = window.innerWidth;
      this.measure();
    };
    window.addEventListener("resize", this.onResize, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onResize);
    this.observer?.disconnect();
  }

  /**
   * In transparent mode the theme hides the header logo outright — its own
   * hero draws a big logo of its own and animates this one in on scroll, via
   * a `--show` class. We have no big logo, so the header keeps its own.
   *
   * The class rather than an inline style, because the rule that hides it
   * carries an extra element in its selector and outranks the one that is
   * meant to bring it back; `--show` is the theme's own way through, and
   * using it means a theme update cannot leave us fighting a stylesheet.
   */
  revealLogo() {
    document
      .querySelectorAll(".wt-header__logo")
      .forEach((logo) => logo.classList.add("wt-header__logo--show"));
  }

  isFirstSection() {
    const own = this.closest(".shopify-section");
    const main = own?.parentElement;
    if (!main) return false;
    const first = Array.from(main.children).find(
      (el) => el.classList.contains("shopify-section") && el.offsetHeight > 0
    );
    return first === own;
  }

  /**
   * How far down the header ends, measured rather than assumed: the
   * announcement bar can be switched off, run to two lines, or change height
   * with the logo setting.
   *
   * From the layout box, not the viewport rect. The header is sticky, so
   * once it detaches its `getBoundingClientRect()` describes where it is on
   * screen rather than where it sits in the document — add the scroll
   * position to that and the answer changes as you scroll. `offsetTop` keeps
   * the flow position a sticky element never gives up.
   */
  measure() {
    const offset = this.header.offsetTop + this.header.offsetHeight;
    if (offset === this.offset) return;
    this.offset = offset;
    this.style.setProperty("--sprayo-hero-offset", offset + "px");
  }

  /* Transparent while the hero is on screen, normal once it is not — without
     this the header stays white-on-white over the sections below. */
  observe() {
    const TRANSPARENT = "wt-header--transparent";
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.header.classList.toggle(TRANSPARENT, entry.isIntersecting);
        });
      },
      { root: null, threshold: 0.05 }
    );
    this.observer.observe(this);
  }
}

if (!customElements.get("sprayo-hero")) {
  customElements.define("sprayo-hero", SprayoHero);
}
